import { 
  collection, doc, getDoc, setDoc, getDocs, updateDoc,
  query, where, serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

// User roles
export const USER_ROLES = {
  ADMIN: 'admin',
  DISTRIBUTOR: 'distributor'
};

// Admin emails - these users are auto-approved and get admin role
const ADMIN_EMAILS = [
  'admin@example.com',
  'adelawad1@gmail.com',
];

// Create or update user profile
export const createUserProfile = async (user, role = USER_ROLES.DISTRIBUTOR) => {
  try {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    const isAdminEmail = ADMIN_EMAILS.includes(user.email?.toLowerCase());
    const assignedRole = isAdminEmail ? USER_ROLES.ADMIN : role;

    if (!userDoc.exists()) {
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        role: assignedRole,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        isActive: true,
        isApproved: isAdminEmail // Admins auto-approved, distributors need approval
      });
    } else {
      const existingData = userDoc.data();
      const updateData = { lastLoginAt: serverTimestamp() };
      if (isAdminEmail && existingData.role !== USER_ROLES.ADMIN) {
        updateData.role = USER_ROLES.ADMIN;
        updateData.isApproved = true;
      }
      await updateDoc(userRef, updateData);
    }
    return await getUserProfile(user.uid);
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

// Approve distributor (admin only)
export const approveUser = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { isApproved: true, approvedAt: serverTimestamp() });
  } catch (error) {
    console.error('Error approving user:', error);
    throw error;
  }
};

// Get pending distributors (not yet approved)
export const getPendingDistributors = async () => {
  try {
    const q = query(
      collection(db, 'users'),
      where('role', '==', USER_ROLES.DISTRIBUTOR),
      where('isApproved', '==', false)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching pending distributors:', error);
    throw error;
  }
};

// Get user profile
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

// Check if user is admin
export const isUserAdmin = async (userId) => {
  try {
    const profile = await getUserProfile(userId);
    return profile?.role === USER_ROLES.ADMIN;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// Get all users (admin only)
export const getAllUsers = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'users'));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

// Get all distributors
export const getDistributors = async () => {
  try {
    const q = query(
      collection(db, 'users'),
      where('role', '==', USER_ROLES.DISTRIBUTOR)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching distributors:', error);
    throw error;
  }
};

// Update user role (admin only)
export const updateUserRole = async (userId, newRole) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: newRole,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

// Deactivate user (admin only)
export const deactivateUser = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isActive: false,
      deactivatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deactivating user:', error);
    throw error;
  }
};

// Activate user (admin only)
export const activateUser = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isActive: true,
      activatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error activating user:', error);
    throw error;
  }
};

// Get user statistics
export const getUserStats = async () => {
  try {
    const users = await getAllUsers();
    return {
      total: users.length,
      admins: users.filter(u => u.role === USER_ROLES.ADMIN).length,
      distributors: users.filter(u => u.role === USER_ROLES.DISTRIBUTOR).length,
      active: users.filter(u => u.isActive !== false).length,
      inactive: users.filter(u => u.isActive === false).length
    };
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
};
